import React, { useState, useEffect, useMemo } from "react";
import {
    useTheme,
    InputAdornment,
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Autocomplete,
    Pagination,
    Rating,
    IconButton,
    ImageList,
    ImageListItem,
    Avatar,
    Divider,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import ModuleSettingsModal from "../components/ModuleSettingsModal";

// StatCard Component for displaying statistics
function StatCard({ label, count, IconComponent, active = false, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                height: "100%",
                minWidth: { xs: '100%', sm: 200 },
                width: "100%",
                borderRadius: 2,
                cursor: onClick ? 'pointer' : 'default',
                background: active
                    ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.4) 0%, rgba(44, 83, 100, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: active ? '1px solid #4CA1AF' : '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: active ? '0 8px 32px rgba(76, 161, 175, 0.25)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                },
            }}
        >
            <CardContent sx={{ minHeight: 80, p: 2 }}>
                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                    <Box
                        sx={{
                            height: 48,
                            width: 48,
                            backgroundColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.15)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                            transition: 'all 0.25s ease',
                            boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
                        }}
                    >
                        <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                                {count}
                            </Typography>
                            {active && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(76, 161, 175, 0.3)',
                                        border: '1px solid rgba(76, 161, 175, 0.6)',
                                        color: '#64d2e2',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Active
                                </Box>
                            )}
                        </Box>
                        <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
                            {label}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function GenerosityTestimonies({ hideHeader = false }) {
    // API Configuration
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';

    // User context
    const { user, isSwitched, effectiveOrgId } = useUser();
    const muiTheme = useTheme();
    const isLight = muiTheme.palette.mode === 'light';
    const [sortOrder, setSortOrder] = useState('asc');

    // State for testimonials list
    const [testimonials, setTestimonials] = useState([]);
    const [filteredTestimonials, setFilteredTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);

    // State for items (for creating testimonials)
    const [items, setItems] = useState([]);

    // Filter states
    const [filterRating, setFilterRating] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [organization, setOrganization] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const itemsPerPage = 9;

    // Statistics
    const [totalTestimonials, setTotalTestimonials] = useState(0);
    const [averageRating, setAverageRating] = useState(0);
    const [highRatedCount, setHighRatedCount] = useState(0);

    // Dialog states
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [selectedTestimony, setSelectedTestimony] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Form states for adding testimony
    const [selectedItem, setSelectedItem] = useState(null);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [formError, setFormError] = useState("");

    // Toast notification
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Image preview dialog
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    // Fetch organizations
    useEffect(() => {
        fetchOrganizations();
    }, []);

    // Fetch testimonials
    useEffect(() => {
        fetchTestimonials();
    }, [organization, effectiveOrgId]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [testimonials, filterRating, searchQuery, fromDate, toDate]);

    const fetchOrganizations = async () => {
        try {
            const response = await fetch(`${API_URL}roles-organizations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });
            } else {
                setOrganizations(data.organizations || []);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchTestimonials = async () => {
        try {
            setLoading(true);
            const targetOrg = effectiveOrgId || (organization?.id ? organization.id : null);
            const orgParam = targetOrg ? `?org_id=${targetOrg}` : '';
            const response = await fetch(`${API_URL}generosity-testimonials${orgParam}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();

            if (!response.ok || data.success === false) {
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }
                setToast({ open: true, message: data?.message || 'Failed to fetch testimonials', severity: "error" });
            } else {
                const testimoniesData = data.data || [];
                setTestimonials(testimoniesData);
                calculateStatistics(testimoniesData);
            }
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            setToast({ open: true, message: 'Error fetching testimonials', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // const fetchItems = async () => {
    //     try {
    //         const response = await fetch(`${API_URL}items`, {
    //             method: 'GET',
    //             headers: {
    //                 'Authorization': `Bearer ${localStorage.getItem('token')}`,
    //             },
    //         });
    //         const data = await response.json();

    //         if (data.success) {
    //             setItems(data.data || []);
    //         }
    //     } catch (error) {
    //         console.error('Error fetching items:', error);
    //     }
    // };

    const calculateStatistics = (data) => {
        setTotalTestimonials(data.length);

        if (data.length > 0) {
            const avgRating = data.reduce((sum, t) => sum + (t.rating || 0), 0) / data.length;
            setAverageRating(avgRating.toFixed(1));

            const highRated = data.filter(t => t.rating >= 4).length;
            setHighRatedCount(highRated);
        } else {
            setAverageRating(0);
            setHighRatedCount(0);
        }
    };

    const applyFilters = () => {
        let filtered = [...testimonials];

        // Filter by rating
        if (filterRating) {
            filtered = filtered.filter(t => t.rating === parseInt(filterRating));
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(t => {
                const comment = t.comment?.toLowerCase() || '';
                const itemTitle = t.give_items?.title?.toLowerCase() || '';
                const firstName = t.app_users?.first_name?.toLowerCase() || '';
                const lastName = t.app_users?.last_name?.toLowerCase() || '';
                const fullName = `${firstName} ${lastName}`.trim();

                return comment.includes(query) ||
                    itemTitle.includes(query) ||
                    firstName.includes(query) ||
                    lastName.includes(query) ||
                    fullName.includes(query);
            });
        }

        // Filter by date range
        if (fromDate) {
            const from = new Date(fromDate);
            filtered = filtered.filter(t => new Date(t.created_at) >= from);
        }
        if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            filtered = filtered.filter(t => new Date(t.created_at) <= to);
        }

        setFilteredTestimonials(filtered);
        setPage(1); // Reset to first page when filters change
    };

    const handleViewTestimony = (testimony) => {
        setSelectedTestimony(testimony);
        setOpenViewDialog(true);
    };

    const handleOpenAddDialog = () => {
        setOpenAddDialog(true);
        setFormError("");
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setSelectedItem(null);
        setRating(5);
        setComment("");
        setImages([]);
        setImagePreviews([]);
        setFormError("");
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        if (files.length + images.length > 3) {
            setFormError("You can upload a maximum of 3 images");
            return;
        }

        setImages([...images, ...files]);

        // Create previews
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImagePreviews([...imagePreviews, ...newPreviews]);
        setFormError("");
    };

    const handleRemoveImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        setImages(newImages);
        setImagePreviews(newPreviews);
    };

    const handleSubmitTestimony = async (e) => {
        e.preventDefault();

        if (!selectedItem) {
            setFormError("Please select an item");
            return;
        }

        if (!comment.trim()) {
            setFormError("Please enter your testimony");
            return;
        }

        try {
            const formData = new FormData();
            formData.append('item_id', selectedItem.id);
            formData.append('rating', rating);
            formData.append('comment', comment);

            // Append images
            images.forEach((image) => {
                formData.append('images', image);
            });

            const response = await fetch(`${API_URL}testimonials`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                // 🔑 Handle invalid token
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });

            }

            else {
                setToast({ open: true, message: 'Testimony submitted successfully!', severity: 'success' });
                handleCloseAddDialog();
                fetchTestimonials();
            }

        } catch (error) {
            console.error('Error submitting testimony:', error);
            setToast({ open: true, message: 'Error submitting testimony', severity: 'error' });
        }
    };

    const handleDeleteTestimony = async (testimonyId) => {
        // Confirm before deleting
        if (!window.confirm('Are you sure you want to delete this testimony? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}delete-testimonial`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: testimonyId }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                // 🔑 Handle invalid token
                if (data.message === 'Invalid token') {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                    return;
                }

                setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });

            }

            else {
                setToast({ open: true, message: 'Testimony deleted successfully!', severity: 'success' });
                fetchTestimonials();
            }

        } catch (error) {
            console.error('Error deleting testimony:', error);
            setToast({ open: true, message: 'Error deleting testimony', severity: 'error' });
        }
    };

    const handleClearFilters = () => {
        setFilterRating("");
        setSearchQuery("");
        setOrganization(null);
    };

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setOpenImageDialog(true);
    };

    // Pagination
    const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);
    const paginatedTestimonials = filteredTestimonials.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];

    return (
        <>
            {!hideHeader && (
                <Helmet>
                    <title>Share & Accept Testimonies - GPN</title>
                </Helmet>
            )}

            <Box p={hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }}>
                {/* Header */}
                {!hideHeader && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Share & Accept Testimonies
                        </Typography>
                    </Box>
                )}

                                {/* Compact Unified Toolbar */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: 2.5,
                        background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
                        border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    }}
                >
                    {/* Row 1: Title, Count, Search, Sort & Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                            pb: 1.25,
                            borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                                Testimonies
                            </Typography>
                            <Chip
                                label={`${totalTestimonials} Total`}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                                    color: isLight ? "#0D9488" : "#64d2e2",
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                placeholder="Search testimonies..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
                            />

                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                displayEmpty
                                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
                            >
                                <MenuItem value="asc">Title: A → Z (Alphabetical)</MenuItem>
                                <MenuItem value="desc">Title: Z → A</MenuItem>
                                <MenuItem value="newest">Date: Newest First</MenuItem>
                            </Select>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.5, px: 1.2 }}
                            >
                                Clear
                            </Button>

                            {!hideHeader && (
                                <Tooltip title="Settings">
                                    <IconButton size="small" onClick={() => setSettingsOpen(true)} sx={{ color: "#64d2e2" }}>
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Row 2: Status Triage Chips & Secondary Filters */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.25,
                            pt: 1.25,
                        }}
                    >
                        {/* Rating Triage Chips */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {[
                                { key: "", label: "All Ratings", count: totalTestimonials },
                                { key: "5", label: "5 Stars", count: "Top" },
                                { key: "4", label: "4+ Stars", count: highRatedCount },
                            ].map((tab) => {
                                const isActive = filterRating === tab.key;
                                return (
                                    <Chip
                                        key={tab.label}
                                        label={`${tab.label} (${tab.count})`}
                                        size="small"
                                        onClick={() => {
                                            setFilterRating(tab.key);
                                            setPage(1);
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: "0.78rem",
                                            height: 26,
                                            bgcolor: isActive
                                                ? (isLight ? "#0D9488" : "#4CA1AF")
                                                : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                                            color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                                            "&:hover": {
                                                bgcolor: isActive
                                                    ? (isLight ? "#0F766E" : "#3b8b98")
                                                    : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Secondary Filters */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {user?.role === 1 && !isSwitched && (
                                <Autocomplete
                                    size="small"
                                    value={organization}
                                    onChange={(event, newValue) => {
                                        setOrganization(newValue);
                                        setPage(1);
                                    }}
                                    options={orgOptions}
                                    getOptionLabel={(option) => option?.org_name || ""}
                                    renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                                    sx={{ width: 170, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Loading Indicator */}
                {loading && <SectionLoader open={loading} />}

                {/* Testimonies Table List View */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        mt: 2,
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                        bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                        boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                    }}
                >
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : '#1a2636' }}>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 800, width: 60, color: isLight ? '#475569' : '#E2E8F0' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 100, color: isLight ? '#475569' : '#E2E8F0' }}>Photos</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 200, color: isLight ? '#475569' : '#E2E8F0' }}>Shared Item</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 160, color: isLight ? '#475569' : '#E2E8F0' }}>Beneficiary / User</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 140, color: isLight ? '#475569' : '#E2E8F0' }}>Rating</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 240, color: isLight ? '#475569' : '#E2E8F0' }}>Testimony Comment</TableCell>
                                <TableCell sx={{ fontWeight: 800, minWidth: 120, color: isLight ? '#475569' : '#E2E8F0' }}>Date</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 800, minWidth: 110, color: isLight ? '#475569' : '#E2E8F0' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedTestimonials.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            No testimonies found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedTestimonials.map((testimony, idx) => {
                                    const userName = `${testimony.app_users?.first_name || ''} ${testimony.app_users?.last_name || ''}`.trim() || 'Beneficiary';
                                    const hasImages = testimony.images && testimony.images.length > 0;

                                    return (
                                        <TableRow
                                            key={testimony.id || idx}
                                            hover
                                            onClick={() => handleViewTestimony(testimony)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': {
                                                    bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                                                },
                                                '& td': {
                                                    borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                                                    py: 1.5,
                                                    color: isLight ? '#1E293B' : '#F1F5F9',
                                                }
                                            }}
                                        >
                                            <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                                                {((page - 1) * itemsPerPage) + idx + 1}
                                            </TableCell>
                                            <TableCell>
                                                {hasImages ? (
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <Avatar
                                                            src={testimony.images[0]}
                                                            variant="rounded"
                                                            sx={{ width: 44, height: 44, border: '1px solid rgba(0,0,0,0.1)' }}
                                                        />
                                                        {testimony.images.length > 1 && (
                                                            <Chip
                                                                label={`+${testimony.images.length - 1}`}
                                                                size="small"
                                                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                                                            />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: isLight ? '#F1F5F9' : 'rgba(255,255,255,0.05)', color: 'text.secondary' }}>
                                                        <ImageIcon sx={{ fontSize: 22 }} />
                                                    </Avatar>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                                    {testimony.give_items?.title || 'Shared Item'}
                                                </Typography>
                                                {testimony.give_items?.category && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {testimony.give_items.category}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#0D9488', fontSize: '0.8rem', fontWeight: 700 }}>
                                                        {userName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                                        {userName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Rating value={testimony.rating || 0} readOnly size="small" precision={0.5} />
                                                    <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706' }}>
                                                        {testimony.rating || 0}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        maxWidth: 300,
                                                        lineHeight: 1.3
                                                    }}
                                                >
                                                    {testimony.comment}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.6}>
                                                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {testimony.created_at ? new Date(testimony.created_at).toLocaleDateString() : '—'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                    <Tooltip title="View Details & Photos">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewTestimony(testimony)}
                                                            aria-label="View"
                                                            sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteTestimony(testimony.id)}
                                                            aria-label="Delete"
                                                            sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 0 && (
                    <Box display="flex" justifyContent="center" mt={3} width="100%">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* View Testimony Dialog */}
                <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} fullWidth maxWidth="md">
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Testimony Details</Typography>
                            <IconButton onClick={() => setOpenViewDialog(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedTestimony && (
                            <Box>
                                {/* Item Information */}
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Item: {selectedTestimony.give_items?.title || 'N/A'}
                                </Typography>

                                {/* User Information */}
                                <Box display="flex" alignItems="center" mb={2} gap={0.5}>
                                    <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography variant="body1" color="text.secondary">
                                        <strong>Submitted by:</strong> {selectedTestimony.app_users?.first_name || ''} {selectedTestimony.app_users?.last_name || ''}
                                    </Typography>
                                </Box>

                                {/* Rating */}
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Typography variant="body2" mr={1}>Rating:</Typography>
                                    <Rating value={selectedTestimony.rating || 0} readOnly />
                                    <Typography variant="body2" ml={1}>
                                        ({selectedTestimony.rating || 0}/5)
                                    </Typography>
                                </Box>

                                {/* Comment */}
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Testimony:
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {selectedTestimony.comment}
                                </Typography>

                                {/* Images */}
                                {selectedTestimony.images && selectedTestimony.images.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Images:
                                        </Typography>
                                        <ImageList cols={3} gap={8}>
                                            {selectedTestimony.images.map((img, idx) => (
                                                <ImageListItem key={idx}>
                                                    <img
                                                        src={img}
                                                        alt={`Testimony ${idx + 1}`}
                                                        loading="lazy"
                                                        style={{ cursor: 'pointer', borderRadius: 4 }}
                                                        onClick={() => handleImageClick(img)}
                                                    />
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    </>
                                )}

                                {/* Metadata */}
                                <Box mt={2}>
                                    <Typography variant="caption" color="text.secondary">
                                        Submitted on: {new Date(selectedTestimony.created_at).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Add Testimony Dialog */}
                <Dialog open={openAddDialog} onClose={handleCloseAddDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Add New Testimony</DialogTitle>
                    <DialogContent>
                        <form id="testimony-form" onSubmit={handleSubmitTestimony}>
                            <Box mt={2}>
                                {/* Select Item */}
                                <Autocomplete
                                    value={selectedItem}
                                    onChange={(event, newValue) => setSelectedItem(newValue)}
                                    options={items}
                                    getOptionLabel={(option) => option?.title || ""}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Item *" fullWidth margin="dense" />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />

                                {/* Rating */}
                                <Box mt={2}>
                                    <Typography component="legend">Rating *</Typography>
                                    <Rating
                                        value={rating}
                                        onChange={(event, newValue) => setRating(newValue)}
                                        size="large"
                                    />
                                </Box>

                                {/* Comment */}
                                <TextField
                                    label="Your Testimony *"
                                    multiline
                                    rows={4}
                                    fullWidth
                                    margin="dense"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    error={!!formError}
                                    helperText={formError}
                                />

                                {/* Image Upload */}
                                <Box mt={2}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<ImageIcon />}
                                        fullWidth
                                    >
                                        Upload Images (Max 3)
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            multiple
                                            onChange={handleImageChange}
                                        />
                                    </Button>

                                    {/* Image Previews */}
                                    {imagePreviews.length > 0 && (
                                        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                                            {imagePreviews.map((preview, idx) => (
                                                <Box key={idx} position="relative">
                                                    <Avatar
                                                        src={preview}
                                                        variant="rounded"
                                                        sx={{ width: 80, height: 80 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveImage(idx)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAddDialog}>Cancel</Button>
                        <Button
                            type="submit"
                            form="testimony-form"
                            variant="contained"
                            color="primary"
                            sx={{ background: '#177373' }}
                        >
                            Submit Testimony
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Image Preview Dialog */}
                <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md">
                    <DialogContent sx={{ p: 0 }}>
                        <img
                            src={selectedImage}
                            alt="Preview"
                            style={{ width: '100%', height: 'auto' }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenImageDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Toast Notification */}
                <Toast toast={toast} setToast={setToast} />

                {/* Module Settings Modal */}
                <ModuleSettingsModal
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    moduleKey="generosityTestimony"
                    moduleTitle="Generosity Testimonies"
                />
            </Box>
        </>
    );
}
 